import {
  Box,
  CircularProgress,
  Drawer,
  Typography,
  Divider,
  IconButton,
  Link,
  Stack,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { format } from "date-fns";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { useState } from "react";
import { formatCurrency } from "@/utils/format-currency";
import { useChargeback } from "@/hooks/use-chargeback";
import { resolveChargeback } from "@/services/disputes.service";
import { showToast } from "@/redux/actions/toast-actions";
import CustomConfirmDialog from "@/components/custom/custom-confirm-dialog";
import { CustomButton } from "@/components/custom/custom-button";
import { DisputeStatusChip } from "./dispute-status-chip";
import { DisputeReasonDialog } from "./dispute-reason-dialog";
import { CHARGEBACK_STATUS, MERCHANT_RESPONSE_STATUS } from "../../data";

const DrawerContent = styled(Box)(({ theme }) => ({
  width: "33vw",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(3),
  overflowY: "auto",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  fontWeight: 600,
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
  letterSpacing: "0.05em",
  marginBottom: theme.spacing(1),
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  paddingBlock: theme.spacing(1.2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:last-of-type": { borderBottom: "none" },
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  color: theme.palette.text.secondary,
  flex: 1,
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  fontWeight: 500,
  color: theme.palette.text.primary,
  flex: 2,
  textAlign: "right",
  wordBreak: "break-word",
}));

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  try {
    return format(new Date(dateStr), "MMM d, yyyy h:mm a");
  } catch {
    return dateStr;
  }
};

export function ChargebackSheet({ chargeback: rowChargeback, open, onClose }) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [wonDialogOpen, setWonDialogOpen] = useState(false);
  const [lostDialogOpen, setLostDialogOpen] = useState(false);

  const { data: fetchedChargeback, isLoading } = useChargeback(
    open ? rowChargeback?.merchantCustomerDisputeId : undefined,
  );
  const chargeback = fetchedChargeback ?? rowChargeback;
  const id = chargeback?.merchantCustomerDisputeId;

  const resolveMutation = useMutation({
    mutationFn: ({ verdict, note }) => resolveChargeback({ id, verdict, note }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["chargebacks"] });
      queryClient.invalidateQueries({ queryKey: ["chargeback", id] });
      queryClient.invalidateQueries({ queryKey: ["chargebackStats"] });
      dispatch(showToast(res?.message ?? "Dispute resolved", "success"));
    },
    onError: (error) => {
      dispatch(showToast(error?.message ?? "Could not resolve dispute", "error"));
    },
  });

  const canResolve = chargeback?.disputeResolutionStatus === "UNDER_REVIEW";
  const isResolved = ["MERCHANT_WON", "CUSTOMER_WON"].includes(
    chargeback?.disputeResolutionStatus,
  );
  const merchantStatus = chargeback?.merchantStatus;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      SlotProps={{ backdrop: { onClick: onClose } }}
    >
      <DrawerContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              Chargeback Details
              {isLoading && <CircularProgress size={14} thickness={5} />}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Ref: {id ?? "N/A"}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: "grey.50",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Disputed Amount
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {formatCurrency(chargeback?.transactionAmount)}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Due {formatDate(chargeback?.merchantResponseDueDate)}
            </Typography>
          </Box>
          <DisputeStatusChip
            status={chargeback?.disputeResolutionStatus}
            config={CHARGEBACK_STATUS}
          />
        </Box>

        <SectionTitle>Customer & Transaction</SectionTitle>
        <Box sx={{ mb: 3 }}>
          <InfoRow>
            <InfoLabel>Customer</InfoLabel>
            <InfoValue>{chargeback?.customerName ?? "N/A"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Customer Email</InfoLabel>
            <InfoValue>{chargeback?.customerEmail ?? "N/A"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Merchant</InfoLabel>
            <InfoValue>{chargeback?.merchantName ?? "N/A"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Transaction Reference</InfoLabel>
            <InfoValue>{chargeback?.transactionReference ?? "N/A"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Transaction Date</InfoLabel>
            <InfoValue>{formatDate(chargeback?.transactionDate)}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Transaction Fee</InfoLabel>
            <InfoValue>
              {chargeback?.transactionFee ? formatCurrency(chargeback.transactionFee) : "N/A"}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Tracking Number</InfoLabel>
            <InfoValue>{chargeback?.trackingNumber ?? "N/A"}</InfoValue>
          </InfoRow>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <SectionTitle>Reason For Dispute</SectionTitle>
        <Typography variant="body2" sx={{ mb: 3 }}>
          {chargeback?.reasonForDisputeInDetails ?? "N/A"}
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <SectionTitle>Merchant's Response</SectionTitle>
        <Box sx={{ mb: 3 }}>
          <InfoRow>
            <InfoLabel>Response Status</InfoLabel>
            <DisputeStatusChip status={merchantStatus} config={MERCHANT_RESPONSE_STATUS} />
          </InfoRow>
          {merchantStatus === "RESPONSE_NEEDED" && (
            <Typography variant="body2" color="text.secondary" sx={{ pt: 1.2 }}>
              Awaiting the merchant's response on their dashboard.
            </Typography>
          )}
          {merchantStatus === "NO_RESPONSE" && (
            <Typography variant="body2" color="text.secondary" sx={{ pt: 1.2 }}>
              The merchant did not respond before the deadline.
            </Typography>
          )}
          {merchantStatus === "ACCEPTED" && (
            <Box sx={{ pt: 1.2 }}>
              <InfoRow>
                <InfoLabel>Responded</InfoLabel>
                <InfoValue>{formatDate(chargeback?.merchantResponseDate)}</InfoValue>
              </InfoRow>
              {chargeback?.merchantAcceptanceComment && (
                <Box sx={{ pt: 1.2 }}>
                  <InfoLabel>Comment</InfoLabel>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {chargeback.merchantAcceptanceComment}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
          {merchantStatus === "REJECTED" && (
            <Box sx={{ pt: 1.2 }}>
              <InfoRow>
                <InfoLabel>Responded</InfoLabel>
                <InfoValue>{formatDate(chargeback?.merchantResponseDate)}</InfoValue>
              </InfoRow>
              <Box sx={{ pt: 1.2 }}>
                <InfoLabel>Reason</InfoLabel>
                <Typography variant="body2" sx={{ mt: 0.5, mb: 1.5 }}>
                  {chargeback?.merchantRejectionReason ?? "N/A"}
                </Typography>
                {chargeback?.attachment && (
                  <>
                    <InfoLabel>
                      Evidence
                      {chargeback?.merchantRejectionDocumentType
                        ? ` (${chargeback.merchantRejectionDocumentType})`
                        : ""}
                    </InfoLabel>
                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      <Link
                        href={chargeback.attachment}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: "0.85rem" }}
                      >
                        <InsertDriveFileOutlinedIcon sx={{ fontSize: 16 }} />
                        View file
                      </Link>
                    </Stack>
                  </>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {isResolved && (
          <>
            <Divider sx={{ mb: 3 }} />
            <SectionTitle>Admin Decision</SectionTitle>
            <Box sx={{ mb: 3 }}>
              <DisputeStatusChip
                status={chargeback?.disputeResolutionStatus}
                config={CHARGEBACK_STATUS}
              />
              {chargeback?.adminMessage && (
                <Typography variant="body2" sx={{ mt: 1.5 }}>
                  {chargeback.adminMessage}
                </Typography>
              )}
            </Box>
          </>
        )}

        <Box sx={{ flex: 1 }} />

        <Divider sx={{ mb: 2 }} />

        {canResolve && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <CustomButton variantType="destructive" onClick={() => setLostDialogOpen(true)}>
              Customer Wins
            </CustomButton>
            <CustomButton variantType="default" onClick={() => setWonDialogOpen(true)}>
              Merchant Wins
            </CustomButton>
          </Box>
        )}
      </DrawerContent>

      <CustomConfirmDialog
        open={wonDialogOpen}
        onClose={() => setWonDialogOpen(false)}
        title="Rule In Merchant's Favor"
        description="The merchant's evidence stands and no refund will be issued."
        confirmLabel="Confirm Merchant Wins"
        mutation={resolveMutation}
        onConfirm={() => resolveMutation.mutate({ verdict: "MERCHANT_WON" })}
      />

      <DisputeReasonDialog
        open={lostDialogOpen}
        onClose={() => setLostDialogOpen(false)}
        title="Rule In Customer's Favor"
        description="The customer will be refunded, deducted from the merchant's next settlement."
        reasonLabel="Reason for ruling"
        reasonPlaceholder="e.g. Merchant's evidence did not prove delivery..."
        confirmLabel="Confirm Customer Wins"
        mutation={resolveMutation}
        onConfirm={(reason) => resolveMutation.mutate({ verdict: "CUSTOMER_WON", note: reason })}
      />
    </Drawer>
  );
}
